import { createFileRoute, Link } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { apiJson, ApiError } from '@/lib/api';
import { track } from '@/lib/analytics';
import type {
  AdminDeleteHouseholdModeratorDataResponse,
  AdminUserDetailResponse,
} from '@/lib/types';

function parseDetail(err: unknown): string {
  if (err instanceof ApiError && err.bodyText) {
    try {
      const j = JSON.parse(err.bodyText) as { detail?: string };
      if (j.detail) return j.detail;
    } catch {
      /* ignore */
    }
    return err.bodyText;
  }
  return err instanceof Error ? err.message : 'Request failed';
}

function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel,
  pending,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  pending?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !pending) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, pending, onClose]);

  if (!open) return null;

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center p-4'>
      <div
        className='absolute inset-0 bg-black/40'
        aria-hidden
        onClick={() => {
          if (!pending) onClose();
        }}
      />
      <div
        className='relative z-10 bg-nova-card rounded-lg shadow-xl max-w-md w-full p-4 space-y-4 border border-nova-border'
        role='dialog'
        aria-modal='true'
        aria-labelledby='confirm-dialog-title'
        onClick={(e) => e.stopPropagation()}
      >
        <h3
          id='confirm-dialog-title'
          className='text-lg font-semibold text-nova-foreground'
        >
          {title}
        </h3>
        <p className='text-sm text-nova-muted-foreground whitespace-pre-wrap'>{message}</p>
        <div className='flex justify-end gap-2 pt-2'>
          <button
            type='button'
            disabled={pending}
            onClick={onClose}
            className='px-4 py-2 text-sm font-semibold border border-nova-input rounded-md text-nova-foreground hover:bg-nova-accent disabled:opacity-50'
          >
            Cancel
          </button>
          <button
            type='button'
            disabled={pending}
            onClick={onConfirm}
            className='px-4 py-2 text-sm font-semibold rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50'
          >
            {pending ? 'Deleting…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function AdminUserHouseholdsPage() {
  const { userId } = Route.useParams();
  const id = Number(userId);
  const { getToken } = useAuth();

  const userQuery = useQuery({
    queryKey: ['admin', 'user-detail', id],
    queryFn: () => apiJson<AdminUserDetailResponse>(getToken, `/v1/admin/users/${id}`),
  });

  const [confirmHouseholdId, setConfirmHouseholdId] = useState<number | null>(null);
  const [banner, setBanner] = useState<{
    householdId: number;
    ok: boolean;
    text: string;
  } | null>(null);

  useEffect(() => {
    track('Admin User Households Screen', { user_id: id });
  }, [id]);

  const deleteModeratorMutation = useMutation({
    mutationFn: (householdId: number) =>
      apiJson<AdminDeleteHouseholdModeratorDataResponse>(
        getToken,
        `/v1/admin/households/${householdId}/moderator-sessions`,
        { method: 'DELETE' }
      ),
    onSuccess: (data) => {
      setBanner({
        householdId: data.household_id,
        ok: true,
        text:
          data.deleted_session_count === 0
            ? `No Moderator sessions to delete for household #${data.household_id}.`
            : `Deleted ${data.deleted_session_count} Moderator session${
                data.deleted_session_count === 1 ? '' : 's'
              } for household #${data.household_id}.`,
      });
      setConfirmHouseholdId(null);
    },
    onError: (e, householdId) => {
      setBanner({
        householdId,
        ok: false,
        text: parseDetail(e),
      });
      setConfirmHouseholdId(null);
    },
  });

  const d = userQuery.data;

  if (userQuery.isLoading || !d) {
    return (
      <div className='flex justify-center py-12'>
        <div className='animate-spin h-8 w-8 border-2 border-nova-primary border-t-transparent rounded-full' />
      </div>
    );
  }

  if (!d.households.length) {
    return <p className='text-sm text-nova-muted-foreground'>This user has no households.</p>;
  }

  return (
    <div className='space-y-8'>
      {d.households.map((h) => (
        <div key={h.household_id} className='bg-nova-card rounded-lg shadow-sm border border-nova-border overflow-hidden'>
          <div className='px-4 py-3 bg-nova-muted border-b border-nova-border flex flex-wrap justify-between items-center gap-2'>
            <div>
              <h2 className='font-semibold text-nova-foreground'>Household #{h.household_id}</h2>
              <p className='text-xs text-nova-muted-foreground'>
                Created {new Date(h.created_at).toLocaleDateString()} · {h.message_count} messages ·{' '}
                {h.expense_count} expenses
              </p>
            </div>
            <div className='flex gap-2'>
              <Link
                to='/admin/users/$userId/chat'
                params={{ userId }}
                search={{ hid: String(h.household_id) }}
                className='text-xs font-semibold text-nova-primary hover:underline'
              >
                Chat
              </Link>
              <Link
                to='/admin/users/$userId/expenses'
                params={{ userId }}
                search={{ hid: String(h.household_id) }}
                className='text-xs font-semibold text-nova-primary hover:underline'
              >
                Expenses
              </Link>
            </div>
          </div>
          <table className='w-full text-sm'>
            <thead className='bg-nova-muted text-left text-xs uppercase text-nova-muted-foreground'>
              <tr>
                <th className='px-4 py-2'>User ID</th>
                <th className='px-4 py-2'>Name</th>
                <th className='px-4 py-2'>Email</th>
                <th className='px-4 py-2'>Phone</th>
                <th className='px-4 py-2'>Target</th>
              </tr>
            </thead>
            <tbody className='divide-y divide-nova-border'>
              {h.members.map((m) => (
                <tr key={m.user_id} className='hover:bg-nova-accent'>
                  <td className='px-4 py-2 font-mono text-xs'>{m.user_id}</td>
                  <td className='px-4 py-2'>
                    {[m.first_name, m.last_name].filter(Boolean).join(' ') || '—'}
                  </td>
                  <td className='px-4 py-2'>{m.email ?? '—'}</td>
                  <td className='px-4 py-2'>{m.phone_number ?? '—'}</td>
                  <td className='px-4 py-2'>
                    {m.is_target ? (
                      <span className='text-xs font-semibold text-nova-primary'>This user</span>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className='px-4 py-3 border-t border-nova-border bg-nova-muted/50'>
            <h3 className='text-xs font-semibold text-nova-muted-foreground uppercase tracking-wide mb-2'>
              Beneficiaries
            </h3>
            {(h.beneficiaries?.length ?? 0) === 0 ? (
              <p className='text-sm text-nova-muted-foreground'>None in this household.</p>
            ) : (
              <table className='w-full text-sm'>
                <thead className='text-left text-xs uppercase text-nova-muted-foreground'>
                  <tr>
                    <th className='pr-4 py-1.5'>ID</th>
                    <th className='pr-4 py-1.5'>Name</th>
                    <th className='pr-4 py-1.5'>Type</th>
                    <th className='pr-4 py-1.5'>Birthdate</th>
                    <th className='py-1.5'>Created</th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-nova-border'>
                  {(h.beneficiaries ?? []).map((b) => (
                    <tr key={b.id} className='hover:bg-nova-card'>
                      <td className='py-2 font-mono text-xs'>{b.id}</td>
                      <td className='py-2 font-medium text-nova-foreground'>{b.name}</td>
                      <td className='py-2'>{b.type}</td>
                      <td className='py-2 tabular-nums'>{b.birthdate ?? '—'}</td>
                      <td className='py-2 text-nova-muted-foreground text-xs'>
                        {new Date(b.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <div className='px-4 py-3 border-t border-nova-border bg-nova-card space-y-3'>
            <h3 className='text-xs font-semibold text-nova-muted-foreground uppercase tracking-wide'>
              Tools
            </h3>
            {banner?.householdId === h.household_id ? (
              <div
                className={`rounded-lg border px-4 py-3 text-sm ${
                  banner.ok
                    ? 'bg-green-50 border-green-200 text-green-900'
                    : 'bg-red-50 border-red-200 text-red-800'
                }`}
              >
                {banner.text}
              </div>
            ) : null}
            <p className='text-sm text-nova-muted-foreground'>
              Permanently delete every Moderator session for this household so you
              can start a fresh test. Shared chat messages from agreements are not
              removed.
            </p>
            <button
              type='button'
              disabled={deleteModeratorMutation.isPending}
              onClick={() => {
                setBanner(null);
                setConfirmHouseholdId(h.household_id);
                track('Admin Delete Household Moderator Data Clicked', {
                  user_id: id,
                  household_id: h.household_id,
                });
              }}
              className='px-4 py-2 text-sm font-semibold rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50'
            >
              Delete all Moderator data
            </button>
          </div>
        </div>
      ))}

      <div className='bg-nova-card rounded-lg shadow-sm border border-nova-border p-4'>
        <h2 className='text-sm font-semibold text-nova-foreground mb-3'>Professional invites</h2>
        {d.professional_invites.length === 0 ? (
          <p className='text-sm text-nova-muted-foreground'>None.</p>
        ) : (
          <table className='w-full text-sm'>
            <thead className='bg-nova-muted text-left text-xs uppercase text-nova-muted-foreground'>
              <tr>
                <th className='px-4 py-2'>ID</th>
                <th className='px-4 py-2'>Household</th>
                <th className='px-4 py-2'>Co-parent user</th>
                <th className='px-4 py-2'>Professional email</th>
                <th className='px-4 py-2'>Pro user ID</th>
                <th className='px-4 py-2'>Status</th>
              </tr>
            </thead>
            <tbody className='divide-y divide-nova-border'>
              {d.professional_invites.map((inv) => (
                <tr key={inv.id} className='hover:bg-nova-accent'>
                  <td className='px-4 py-2'>{inv.id}</td>
                  <td className='px-4 py-2'>{inv.household_id}</td>
                  <td className='px-4 py-2'>{inv.coparent_user_id}</td>
                  <td className='px-4 py-2'>{inv.professional_email}</td>
                  <td className='px-4 py-2'>{inv.professional_user_id ?? '—'}</td>
                  <td className='px-4 py-2'>
                    <span className='text-xs font-medium'>{inv.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <ConfirmDialog
        open={confirmHouseholdId !== null}
        title='Delete all Moderator data?'
        message={
          confirmHouseholdId == null
            ? ''
            : `This permanently deletes every Moderator session for household #${confirmHouseholdId}.\n\nYou cannot undo this. Use it to reset a household for testing.`
        }
        confirmLabel='Delete all sessions'
        pending={deleteModeratorMutation.isPending}
        onClose={() => {
          if (!deleteModeratorMutation.isPending) setConfirmHouseholdId(null);
        }}
        onConfirm={() => {
          if (confirmHouseholdId == null) return;
          track('Admin Delete Household Moderator Data Confirmed', {
            user_id: id,
            household_id: confirmHouseholdId,
          });
          deleteModeratorMutation.mutate(confirmHouseholdId);
        }}
      />
    </div>
  );
}

export const Route = createFileRoute('/admin/users/$userId/households')({
  validateSearch: (search: Record<string, unknown>) => ({
    hid: typeof search.hid === 'string' ? search.hid : undefined,
  }),
  component: AdminUserHouseholdsPage,
});
